/**
 * Frontend mirror of the backend per-category field policy
 * (`procurtal-backend/src/categories/data/field-config.ts`).
 *
 * Kept in sync by hand until `npm run generate:api` brings the DTO types over
 * — at which point the FieldKey union and DTOs can be imported from `src/api`
 * and this file reduced to just the labels/kind UI metadata.
 */
export const KNOWN_FIELD_KEYS = [
  "screenSize",
  "resolution",
  "ram",
  "cpu",
  "storage",
  "color",
  "condition",
  "dealType",
  "warranty",
  "warrantyType",
  "warrantyProvider",
  "model",
  "notes",
  "datasheet",
] as const;

export type FieldKey = (typeof KNOWN_FIELD_KEYS)[number];

export interface FieldPolicy {
  visible: boolean;
  required: boolean;
}

/** Effective (post-inheritance) policy for every configurable field. */
export type EffectiveFieldConfig = Record<FieldKey, FieldPolicy>;

export const DEFAULT_FIELD_POLICY: FieldPolicy = { visible: true, required: false };

export const FIELD_LABELS: Record<FieldKey, string> = {
  screenSize: "Screen size",
  resolution: "Resolution",
  ram: "Memory (RAM)",
  cpu: "CPU",
  storage: "Storage",
  color: "Color",
  condition: "Condition",
  dealType: "Deal type",
  warranty: "Warranty",
  warrantyType: "Warranty type",
  warrantyProvider: "Warranty provider",
  model: "Model",
  notes: "Notes",
  datasheet: "Datasheet",
};

export const FIELD_KIND: Record<FieldKey, "spec" | "axis"> = {
  screenSize: "spec",
  resolution: "spec",
  ram: "axis",
  cpu: "axis",
  storage: "axis",
  color: "axis",
  condition: "spec",
  dealType: "spec",
  warranty: "spec",
  warrantyType: "spec",
  warrantyProvider: "spec",
  model: "spec",
  notes: "spec",
  datasheet: "spec",
};

/** Policy where every configurable field is visible and optional. */
export function defaultEffectiveConfig(): EffectiveFieldConfig {
  return KNOWN_FIELD_KEYS.reduce((acc, key) => {
    acc[key] = { ...DEFAULT_FIELD_POLICY };
    return acc;
  }, {} as EffectiveFieldConfig);
}

/** Tri-state UI value derived from a policy. */
export type FieldState = "hidden" | "optional" | "required";

export function policyToState(policy: FieldPolicy): FieldState {
  if (!policy.visible) return "hidden";
  return policy.required ? "required" : "optional";
}

export function stateToPolicy(state: FieldState): FieldPolicy {
  switch (state) {
    case "hidden":
      return { visible: false, required: false };
    case "required":
      return { visible: true, required: true };
    default:
      return { visible: true, required: false };
  }
}
